import { create } from 'zustand';
import type { FieldMemo } from '../types';
import { memoService } from '../services/memoService';
import { withLoading } from './storeHelper';

interface MemoStore {
  /** 圃場 ID ごとのメモ一覧（API 取得結果をそのまま保持） */
  memosByFieldId: Record<string, FieldMemo[]>;

  /** ローディング状態 */
  loading: boolean;

  /** エラーメッセージ */
  error: string | null;

  /**
   * 指定した圃場のメモを取得する。
   * キャッシュ済みの場合は再取得しない。
   *
   * @param fieldId 圃場 ID
   */
  fetchMemosByFieldId: (fieldId: string) => Promise<void>;

  /**
   * 複数圃場のメモを一括取得する。未取得の圃場のみ API を呼ぶ。
   *
   * @param fieldIds 圃場 ID の配列
   */
  fetchMemosByFieldIds: (fieldIds: string[]) => Promise<void>;

  /**
   * 圃場メモを追加する。成功時はストアにも追加する。
   *
   * @param params fieldId（対象圃場 ID） と content（本文）
   */
  addMemo: (params: { fieldId: string; content: string }) => Promise<void>;

  /**
   * 指定した圃場 ID 群に紐づくメモを作成日時降順で返す（同期）。
   *
   * @param fieldIds 圃場 ID の配列
   * @returns メモの配列（createdAt 降順）
   */
  getMemosByFieldIds: (fieldIds: string[]) => FieldMemo[];

  /**
   * キャッシュをクリアする。
   */
  clearCache: () => void;
}

/**
 * メモデータを管理するZustandストア。
 *
 * バックエンドの `/api/v1/memos` を呼び出して圃場メモの一覧取得・追加を提供する。
 */
export const useMemoStore = create<MemoStore>((set, get) => ({
  memosByFieldId: {},
  loading: false,
  error: null,

  fetchMemosByFieldId: async (fieldId: string) => {
    if (get().memosByFieldId[fieldId]) {
      return;
    }

    await withLoading(
      set,
      async () => {
        const memos = await memoService.list('FIELD', fieldId);
        set((state) => ({
          memosByFieldId: { ...state.memosByFieldId, [fieldId]: memos },
        }));
      },
      'メモの取得に失敗しました'
    );
  },

  fetchMemosByFieldIds: async (fieldIds: string[]) => {
    const target = fieldIds.filter((id) => !get().memosByFieldId[id]);
    if (target.length === 0) return;

    // 1 圃場の失敗で全体が落ちないよう allSettled を使い、成功分は反映しつつ
    // 失敗があれば最初のエラーを error として記録する。
    set({ loading: true, error: null });
    const settled = await Promise.allSettled(
      target.map(async (id) => ({ id, memos: await memoService.list('FIELD', id) }))
    );

    const successes: { id: string; memos: FieldMemo[] }[] = [];
    let firstError: unknown = null;
    for (const result of settled) {
      if (result.status === 'fulfilled') {
        successes.push(result.value);
      } else if (firstError === null) {
        firstError = result.reason;
      }
    }

    if (successes.length > 0) {
      // レースコンディション対策: fetch 開始後に addMemo で追加されたメモが
      // 古い一覧レスポンスで上書きされないよう、ID ベースでマージする。
      // 取得後に存在しない ID のローカルエントリ（楽観追加分など）は保持する。
      set((state) => {
        const next = { ...state.memosByFieldId };
        for (const { id, memos } of successes) {
          const existing = state.memosByFieldId[id] ?? [];
          const fetchedIds = new Set(memos.map((m) => m.id));
          const locallyAdded = existing.filter((m) => !fetchedIds.has(m.id));
          next[id] = [...locallyAdded, ...memos].sort(
            (a, b) => b.createdAt.getTime() - a.createdAt.getTime()
          );
        }
        return { memosByFieldId: next };
      });
    }

    if (firstError !== null) {
      const msg = firstError instanceof Error ? firstError.message : 'メモの取得に失敗しました';
      set({ loading: false, error: msg });
    } else {
      set({ loading: false });
    }
  },

  addMemo: async ({ fieldId, content }) => {
    // withLoading はエラーを握りつぶすため、UI が成功/失敗を判別できるよう
    // ここでは loading/error の管理を自前で行い、例外はそのまま呼び出し元へ伝播する。
    set({ loading: true, error: null });
    try {
      const created = await memoService.create('FIELD', fieldId, content);
      set((state) => {
        const existing = state.memosByFieldId[fieldId] ?? [];
        return {
          memosByFieldId: {
            ...state.memosByFieldId,
            [fieldId]: [created, ...existing],
          },
          loading: false,
        };
      });
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'メモの追加に失敗しました';
      set({ error: msg, loading: false });
      throw error;
    }
  },

  getMemosByFieldIds: (fieldIds: string[]) => {
    const { memosByFieldId } = get();
    const collected: FieldMemo[] = [];
    for (const id of fieldIds) {
      const memos = memosByFieldId[id];
      if (memos) collected.push(...memos);
    }
    return collected.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  },

  clearCache: () => {
    set({ memosByFieldId: {}, error: null });
  },
}));
